import { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { api } from "@/lib/api";
import { Map as MapIcon } from "lucide-react";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";
const METRICS = [["signups", "Signups"], ["prospects", "Prospects found"], ["leads", "Buyer leads"], ["visits", "Visits"]];

export function CountryHeatmap() {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("signups");
  const [days, setDays] = useState(90);
  const [hover, setHover] = useState(null);
  useEffect(() => { api.get("/discovery/geo-stats", { params: { days } }).then((r) => setData(r.data)).catch(() => setData({ rows: [], totals: {} })); }, [days]);

  const byNumeric = useMemo(() => Object.fromEntries((data?.rows || []).map((r) => [String(r.numeric).padStart(3, "0"), r])), [data]);
  const max = useMemo(() => Math.max(1, ...(data?.rows || []).map((r) => r[metric] || 0)), [data, metric]);
  const fill = (v) => { if (!v) return "#1a1a1d"; const t = Math.sqrt(v / max); return `rgba(255,59,48,${0.18 + 0.82 * t})`; };
  const top = useMemo(() => [...(data?.rows || [])].filter((r) => r[metric]).sort((a, b) => b[metric] - a[metric]).slice(0, 8), [data, metric]);

  if (!data) return null;
  return (
    <div className="mt-10 rounded-xl border border-[#27272A] bg-[#121212] p-6" data-testid="country-heatmap-card">
      <div className="flex flex-wrap items-center gap-3">
        <MapIcon className="h-5 w-5 text-[#FF3B30]" />
        <h2 className="font-black text-xl tracking-tight">Where the world meets VISMARKS</h2>
        <span className="text-xs text-neutral-500" data-testid="heatmap-countries">{data.countries} countries · last {days} days</span>
        <div className="ml-auto flex items-center gap-2">
          {METRICS.map(([k, l]) => <button key={k} onClick={() => setMetric(k)} data-testid={`heatmap-metric-${k}`} className={`px-3 h-8 rounded-full text-xs font-bold border transition-colors ${metric === k ? "bg-[#FF3B30] border-[#FF3B30] text-white" : "border-[#27272A] text-neutral-400 hover:border-neutral-500"}`}>{l} <span className="font-mono opacity-70">{data.totals?.[k] ?? 0}</span></button>)}
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="heatmap-days" className="h-8 rounded-md bg-[#0A0A0A] border border-[#27272A] px-2 text-xs text-white focus:outline-none"><option value={30}>30 days</option><option value={90}>90 days</option><option value={365}>1 year</option></select>
        </div>
      </div>
      <div className="mt-4 grid lg:grid-cols-[1fr_260px] gap-4">
        <div className="relative rounded-lg border border-[#27272A] bg-[#0A0A0A] overflow-hidden" data-testid="heatmap-map">
          <ComposableMap projectionConfig={{ scale: 150 }} style={{ width: "100%", height: "auto" }}>
            <ZoomableGroup center={[10, 10]} zoom={1}>
              <Geographies geography={GEO_URL}>
                {({ geographies }) => geographies.map((g) => { const r = byNumeric[g.id]; const v = r?.[metric] || 0; return (
                  <Geography key={g.rsmKey} geography={g} onMouseEnter={() => setHover(r ? { ...r, name: g.properties.name } : { name: g.properties.name })} onMouseLeave={() => setHover(null)}
                    style={{ default: { fill: fill(v), stroke: "#27272A", strokeWidth: 0.4, outline: "none" }, hover: { fill: v ? "#ff6b61" : "#2a2a2e", stroke: "#fff", strokeWidth: 0.6, outline: "none" }, pressed: { outline: "none" } }} />); })}
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
          {hover && (
            <div className="absolute left-3 bottom-3 rounded-md border border-[#27272A] bg-[#0E0E10]/95 px-3 py-2 text-xs" data-testid="heatmap-tooltip">
              <div className="font-bold text-white">{hover.name}</div>
              <div className="text-neutral-400 font-mono">signups {hover.signups ?? 0} · prospects {hover.prospects ?? 0} · leads {hover.leads ?? 0} · visits {hover.visits ?? 0}</div>
            </div>
          )}
        </div>
        <div className="rounded-lg border border-[#27272A] bg-[#0A0A0A] p-3" data-testid="heatmap-top">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-2">Top countries · {METRICS.find((m) => m[0] === metric)[1]}</div>
          {top.length === 0 ? <p className="text-xs text-neutral-600">No data yet for this period.</p> : top.map((r, i) => (
            <div key={r.code} className="flex items-center gap-2 py-1 text-xs" data-testid={`heatmap-top-${r.code}`}>
              <span className="w-4 text-neutral-600 font-mono">{i + 1}</span><span className="flex-1 text-neutral-200 truncate">{r.label}</span>
              <div className="w-20 h-1.5 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-[#FF3B30]" style={{ width: `${Math.max(4, (r[metric] / max) * 100)}%` }} /></div>
              <span className="w-10 text-right font-mono text-white">{r[metric]}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
